import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReviewPage } from "../../../../components/Learner/ReviewPage";
import { getLastOpenedUrl } from "../../../../utils/lastOpened";

// Wrapper component for route
const ReviewPageRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get review data from location state or use defaults
  const {
    courseType: stateCourseType = "Quick Practice",
    score: stateScore = 33,
    currentLevel: stateCurrentLevel = 4.29,
    questions: stateQuestions = [],
    userAnswers: stateUserAnswers = [], // Raw user answers array (from practice submission)
    fromPage = "/learner/result", // Track where user came from
    apiResponse: stateApiResponse = null, // Full API response from SUBMIT API (for accuracy, soals-stats)
    resultPageState = null, // All result page data to restore when going back
  } = location.state || {};

  // Load data from localStorage as fallback
  const localStorageData = useMemo(() => {
    try {
      // Load user-answers from localStorage
      const userAnswersRaw = localStorage.getItem("user-answers");
      let userAnswersFromStorage = [];
      let sessionIdFromStorage = null;
      let practiceTypeFromStorage = null;

      if (userAnswersRaw) {
        const parsed = JSON.parse(userAnswersRaw);
        userAnswersFromStorage = parsed["user-answers"] || [];
        sessionIdFromStorage = parsed["session-id"];
        practiceTypeFromStorage = parsed["practice-type"];
      }

      // Load qp-session from localStorage (questions from GET API)
      const qpSessionRaw = localStorage.getItem("qp-session");
      let questionsFromStorage = [];

      if (qpSessionRaw) {
        const parsed = JSON.parse(qpSessionRaw);
        const problems = parsed.problems || [];

        // Convert problems to ReviewPage format
        questionsFromStorage = problems.map((problem) => {
          // Build question object with originalQuestion
          // Spread all fields from problem to ensure nothing is lost
          return {
            type: problem.type || "pilgan-single",
            originalQuestion: {
              ...problem, // Include all fields from problem
              // Ensure arrays are always arrays (not null/undefined)
              choices: problem.choices || [],
              options: problem.options || problem.choices || [],
              answers: problem.answers || [],
              annotations: problem.annotations || [],
              questions: problem.questions || problem["sub-questions"] || [],
              "sub-questions":
                problem["sub-questions"] || problem.questions || [],
            },
          };
        });

        console.log("💾 Loaded qp-session from localStorage:", {
          problemsCount: problems.length,
          questionsCount: questionsFromStorage.length,
        });
      }

      return {
        userAnswers: userAnswersFromStorage,
        questions: questionsFromStorage,
        sessionId: sessionIdFromStorage,
        practiceType: practiceTypeFromStorage,
      };
    } catch (error) {
      console.warn("⚠️ Failed to load data from localStorage:", error);
      return {
        userAnswers: [],
        questions: [],
        sessionId: null,
        practiceType: null,
      };
    }
  }, []);

  // Use state data if available, otherwise use localStorage data
  const questions =
    stateQuestions.length > 0 ? stateQuestions : localStorageData.questions;
  const userAnswers =
    stateUserAnswers.length > 0
      ? stateUserAnswers
      : localStorageData.userAnswers;
  const courseType =
    stateCourseType ||
    (localStorageData.practiceType === "quick-practice"
      ? "Quick Practice"
      : "Quick Practice");
  const score = stateScore;
  const currentLevel = stateCurrentLevel;
  const apiResponse = stateApiResponse;

  // Log received data for debugging
  useEffect(() => {
    console.log("📝 Review Page - Received Data:", {
      courseType,
      score,
      currentLevel,
      questionsCount: questions.length,
      userAnswersCount: userAnswers.length,
      questionsSource:
        stateQuestions.length > 0 ? "location.state" : "localStorage",
      userAnswersSource:
        stateUserAnswers.length > 0 ? "location.state" : "localStorage",
      hasApiResponse: !!apiResponse,
      userAnswers: userAnswers,
      "pilgan-multi entries": userAnswers.filter(
        (ua) => ua?.type === "pilgan-multi" || ua?.type === "mc"
      ),
    });
  }, [
    courseType,
    score,
    currentLevel,
    questions,
    userAnswers,
    apiResponse,
    stateQuestions,
    stateUserAnswers,
  ]);

  // Set body background to white for review page
  useEffect(() => {
    document.body.style.background = "#ffffff";
    document.body.style.backgroundImage = "";
    document.body.style.backgroundSize = "";
    document.documentElement.style.background = "#ffffff";

    // Cleanup: reset background when component unmounts
    return () => {
      document.body.style.background = "";
      document.body.style.backgroundImage = "";
      document.body.style.backgroundSize = "";
      document.documentElement.style.background = "";
    };
  }, []);

  // Get langCode based on fromPage - hardcode 'de' for German, 'en' for English, 'ja' for Japanese
  let langCode = 'de';
  if (fromPage === '/learner/english-language-learning' || fromPage?.includes('english')) {
    langCode = 'en';
  } else if (fromPage === '/learner/japanese-language-learning' || fromPage?.includes('japanese')) {
    langCode = 'ja';
  }

  const handleBack = () => {
    // Navigate back to result page with all original data
    if (
      (fromPage === "/learner/result" ||
        fromPage === "/learner/skill-result") &&
      resultPageState &&
      resultPageState.apiResponse
    ) {
      // Restore all result page data when going back (only if has valid apiResponse)
      console.log("🔙 ReviewPage: Navigating back to result page with state");
      navigate(fromPage, {
        state: resultPageState,
      });
    } else {
      // Navigate back to last opened URL
      const lastOpenedUrl = getLastOpenedUrl();

      if (lastOpenedUrl) {
        console.log(
          "📖 ReviewPage: Navigating back to lastOpenedUrl:",
          lastOpenedUrl
        );
        navigate(lastOpenedUrl);
      } else if (fromPage && fromPage !== "/learner/language-learning") {
        // Fallback to fromPage if it's not the default learning page
        console.log(
          "📖 ReviewPage: No lastOpenedUrl found, using fromPage:",
          fromPage
        );
        navigate(fromPage);
      } else {
        // Last resort: navigate to language learning page
        console.log(
          "📖 ReviewPage: Last resort: Navigating to language learning page"
        );
        navigate("/learner/language-learning");
      }
    }
  };

  return (
    <ReviewPage
      courseType={courseType}
      score={score}
      currentLevel={currentLevel}
      questions={questions}
      userAnswers={userAnswers}
      apiResponse={apiResponse}
      langCode={langCode}
      onBack={handleBack}
    />
  );
};

export { ReviewPageRoute };
export default ReviewPageRoute;
